import { useEffect, useState } from 'react';
import { Box, Container, Paper, styled } from '@mui/material';
import TopSectionRegisterPage from 'pages/RegisterPage/section/TopSectionRegisterPage';
import MiddleSectionRegisterPage from 'pages/RegisterPage/section/MiddleSectionRegisterPage';
import BottomSectionRegisterPage from 'pages/RegisterPage/section/BottomSectionRegisterPage';
import { useSearchParams, useNavigate, createSearchParams } from 'react-router-dom';
import { DocumentIds, Entity, FormValueTypes, SuratKuasa } from 'types/form.type';
import SubmissionService from 'services/SubmissionService';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { formDefaultValue } from 'constants/default-value';
import useSessionStorage from 'hooks/useSessionStorage';
import { removeFalsy, removeURLFromObject } from 'helpers/utils/object';
import { Optional } from 'helpers/utils/type';
import { BeneficialOwnerAddressAnswer } from 'helpers/utils/enumsNameMappings';
import AlertModal from 'components/AlertModal';
import { AuthorisedSignatoriesTerms } from '@xendit/id-open-banking-ekyb-common/dist/enums/submission';

const DEFAULT_VALUE = {
  guestName: '',
  guestEmail: '',
  agreement1Step1: '',
  agreement2Step1: '',
  beneficialOwnerDifferentAddress: '',
  videoCallSchedule: undefined,
  videoCallBooked: undefined,
  ...formDefaultValue,
  surat_kuasa: {
    ...formDefaultValue.surat_kuasa,
    terms: AuthorisedSignatoriesTerms.SINGLE
  }
};

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [savedFormValues, setSavedFormValues] = useSessionStorage('onboarding-form', DEFAULT_VALUE);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const step = Number(searchParams.get('step')) || 1;
  const section = Number(searchParams.get('section')) || 1;

  const formMethods = useForm({
    defaultValues: savedFormValues
  });

  useEffect(() => {
    const beforePageClose = () => {
      setSavedFormValues(formMethods.getValues());
    };

    window.addEventListener('beforeunload', beforePageClose);

    return () => window.removeEventListener('beforeunload', beforePageClose);
  }, [formMethods, setSavedFormValues]);

  const clearAllFormValues = () => {
    // reset all field value and clear session storage
    formMethods.reset();
    sessionStorage.clear();
  };

  const handleStepSubmit: SubmitHandler<FormValueTypes> = async (formValues) => {
    if (step === 1 && 'guestName' in formValues) {
      try {
        setIsLoading(true);
        await SubmissionService.submitStep1({ name: formValues.guestName, email: formValues.guestEmail });

        handleNextStep();
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }

    if (step === 2 && section === 1) {
      try {
        setIsLoading(true);
        const data = await SubmissionService.submitStep2Section1(formValues);
        formMethods.setValue('submissionId', data?.data?.id ?? '');
        formMethods.setValue('videoCallSchedule', undefined);
        formMethods.setValue('videoCallBooked', undefined);

        // the NPWP number already registered, open modal to redirect to main page
        if (data?.status === 302 && data?.data?.redirect_url) {
          setIsAlertModalOpen(true);
          return;
        }

        handleNextStep();
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }

    if (step === 2 && (section === 2 || section === 3)) {
      handleNextStep();
    }

    if (step === 3 && section === 1) {
      handleNextStep();
    }

    if (step === 3 && section === 2) {
      const copyOfEntity: Optional<Entity, 'npwp' | 'izin_usaha'> = removeFalsy(formValues.entity);
      const copyOfSuratKuasa: SuratKuasa = removeFalsy(formValues.surat_kuasa);
      const documentIds = removeURLFromObject<DocumentIds>(copyOfEntity.document_ids);
      const managements = copyOfEntity.managements.map((management) => removeURLFromObject(management));
      const authorised_signatories = copyOfSuratKuasa.authorised_signatories.map((signatory) =>
        removeURLFromObject(signatory)
      );
      const beneficialOwner = removeURLFromObject(copyOfEntity.beneficial_owner);
      const sameAddressAsDomicile =
        formMethods.getValues('beneficialOwnerDifferentAddress') === BeneficialOwnerAddressAnswer.NO;

      delete copyOfEntity.npwp;
      delete copyOfEntity.izin_usaha;

      try {
        setIsLoading(true);
        await SubmissionService.submitStep3({
          id: formValues.submissionId,
          data: {
            entity: {
              ...copyOfEntity,
              beneficial_owner: {
                ...beneficialOwner,
                domicile_address: sameAddressAsDomicile ? beneficialOwner.address : beneficialOwner.domicile_address
              },
              document_ids: {
                ...documentIds
              },
              managements
            },
            surat_kuasa: {
              ...copyOfSuratKuasa,
              authorised_signatories
            }
          }
        });

        await SubmissionService.submitAllForm({ id: formValues.submissionId });

        handleNextStep();
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }

    if (step === 4) {
      try {
        clearAllFormValues();
        navigate({
          pathname: '/registration/success'
        });
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleNextStep = () => {
    let step = Number(searchParams.get('step')) || 1;
    let section = Number(searchParams.get('section')) || 1;
    if ((step === 2 && section < 3) || (step === 3 && section < 2)) {
      section = section + 1;
    } else {
      step = step + 1;
      section = 1;
    }
    navigate({
      pathname: '/registration',
      search: createSearchParams({
        step: step.toString(),
        section: section.toString()
      }).toString()
    });
  };

  return (
    <ContainerStyle>
      <PaperStyled>
        <FormProvider {...formMethods}>
          <Box component="form" autoComplete="off" onSubmit={formMethods.handleSubmit(handleStepSubmit)}>
            <TopSectionRegisterPage />
            <MiddleSectionRegisterPage />
            {![2, 3].includes(step) && <BottomSectionRegisterPage buttonIsLoading={isLoading} />}
            <AlertModal
              open={isAlertModalOpen}
              onClose={() => {
                clearAllFormValues();
                setIsAlertModalOpen(false);
                navigate('/');
              }}
            />
          </Box>
        </FormProvider>
      </PaperStyled>
    </ContainerStyle>
  );
};

export const PaperStyled = styled(Paper)`
  width: 100%;
  padding: 24px;
`;

const ContainerStyle = styled(Container)`
  display: fixed;
  justify-content: center;
  align-items: center;
  width: 100vw;
  min-height: 100vh;
  padding-bottom: 32px;
`;

export default RegisterPage;
